/* eslint-disable react-refresh/only-export-components --
   This is the route-table module (DIT-264 / S7), not a fast-refresh component
   file: it intentionally co-locates small layout/gate components with the
   exported `routes` config. HMR granularity is irrelevant here. */
import { lazy, Suspense, useEffect, type ReactElement } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { RouteRecord } from 'vite-react-ssg';

import { Layout as FunnelLayout } from '@widgets/layout';
import { DEFAULT_LANG, canonicalLang, type Lang } from '@shared/config';

// ── Marketing site (ported 1:1 from done-it-design — DIT-264 / S8) ──────────────
import MarketingLayout from '@/marketing/components/Layout';
import LocaleLayout from '@/marketing/i18n/LocaleLayout';
import { LIVE_LOCALES, DEFAULT_LOCALE } from '@/marketing/i18n/locales';
import { Seo } from '@/marketing/seo/Seo';
import type { PageMetaKey } from '@/marketing/seo/meta';
import HomeNew from '@/marketing/screens/HomeNew';
import FeaturesNew from '@/marketing/screens/FeaturesNew';
import ProjectManagement from '@/marketing/screens/ProjectManagement';
import Law2027 from '@/marketing/screens/Law2027';
import Pricing from '@/marketing/screens/Pricing';
import Industries from '@/marketing/screens/Industries';
import IndustryDetail from '@/marketing/screens/IndustryDetail';
import Resources from '@/marketing/screens/Resources';
import Trainings from '@/marketing/screens/Trainings';
import GettingStarted from '@/marketing/screens/GettingStarted';
import BlogPost from '@/marketing/screens/BlogPost';
import Faq from '@/marketing/screens/Faq';
import Legal from '@/marketing/screens/Legal';
import About from '@/marketing/screens/About';
import Contact from '@/marketing/screens/Contact';
import FreeTrial from '@/marketing/screens/FreeTrial';
import NotFound from '@/marketing/screens/NotFound';

// ── Signup funnel (existing landing — kept CSR-only, excluded from prerender) ────
const PlanPage = lazy(() => import('@pages/plan').then((m) => ({ default: m.PlanPage })));
const ConfirmPage = lazy(() => import('@pages/confirm').then((m) => ({ default: m.ConfirmPage })));
const AccountPage = lazy(() => import('@pages/account').then((m) => ({ default: m.AccountPage })));
const VerifyEmailPage = lazy(() =>
  import('@pages/verify-email').then((m) => ({ default: m.VerifyEmailPage })),
);
const PaymentPage = lazy(() => import('@pages/payment').then((m) => ({ default: m.PaymentPage })));
const DonePage = lazy(() => import('@pages/done').then((m) => ({ default: m.DonePage })));

function Spinner() {
  return (
    <div className="flex flex-1 items-center justify-center py-16" role="status" aria-live="polite">
      <span className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-ds-accent" />
    </div>
  );
}

/**
 * Funnel root. Formerly the MUI provider scope — `ThemeProvider` + `CssBaseline`
 * were confined here so emotion stayed out of the prerendered marketing HTML.
 * No funnel component renders MUI any more (DIT-399), so the providers, the theme
 * and the MUI dependency are gone from this bundle entirely; the shell is the
 * same Tailwind design system the marketing site uses.
 *
 * The per-locale `<title>` is set in `widgets/layout`, which is where the funnel's
 * i18next provider lives — reading it out here would resolve against the mutable
 * global default that the marketing instances swap.
 */
function FunnelRoot() {
  return <FunnelGate />;
}

/** `/` → the marketing home in the best-guess live locale (BE-default `nl`). The
 *  edge geo-redirect (S10) refines this per visitor country; this is the SPA
 *  fallback for direct hits / no-JS-edge. */
function guessMarketingLang(): string {
  const nav = typeof navigator !== 'undefined' ? navigator.language?.toLowerCase() : '';
  const base = nav?.split('-')[0] ?? '';
  const match = LIVE_LOCALES.find((l) => l.code === nav || l.code === base);
  return match?.code ?? DEFAULT_LOCALE;
}

function IndexRedirect() {
  return <Navigate to={`/${guessMarketingLang()}`} replace />;
}

/**
 * Funnel locale gate — locks i18n to the `:lang` prefix against the LIVE
 * marketing locales, which the funnel now shares (DIT-399; it used to check a
 * private 16-code list, so the codes the two registries disagreed on 404'd).
 * Ported from the old AppRouter `LangGate`; renders the funnel shell.
 *
 * A retired funnel-only prefix (`/nl-BE/plan`) is REPLACED by its live locale
 * rather than prefixed, so it lands on `/nl/plan` instead of the dead
 * `/en/nl-BE/plan`. A segment that is no locale at all (Stripe's lang-less
 * `/done?…`, see UC-AUTH-DO-006) still gets the default lang prepended.
 */
function FunnelGate() {
  const { lang } = useParams<{ lang?: string }>();
  const { i18n } = useTranslation();
  const location = useLocation();
  const canonical = canonicalLang(lang);
  const live = canonical !== null && canonical === lang;

  useEffect(() => {
    if (live && lang && i18n.resolvedLanguage !== lang) {
      void i18n.changeLanguage(lang);
    }
  }, [live, lang, i18n]);

  if (live) return <FunnelLayout />;

  const rest = location.pathname.replace(/^\/[^/]*/, '');
  const target = canonical
    ? `/${canonical}${rest}${location.search}`
    : `/${DEFAULT_LANG}${location.pathname}${location.search}`;
  return <Navigate to={target} replace />;
}

/** Wrap a marketing screen with its per-page SEO head (route-level so the screens
 *  stay free of framework-mode `meta` exports). */
const seo = (pageKey: PageMetaKey, el: ReactElement): ReactElement => (
  <>
    <Seo pageKey={pageKey} />
    {el}
  </>
);

export const routes: RouteRecord[] = [
  { index: true, element: <IndexRedirect /> },

  // ── Marketing site (Tailwind, prerendered): shell → :lang (i18n) → screens.
  //    The `:lang/*` splat catch-all is the lowest-priority leaf, so funnel
  //    static routes (`:lang/plan`, …) still win under the same prefix. ─────────
  {
    element: <MarketingLayout />,
    children: [
      {
        path: ':lang',
        element: <LocaleLayout />,
        children: [
          { index: true, element: seo('home', <HomeNew />) },
          { path: 'features', element: seo('features', <FeaturesNew />) },
          { path: 'project-management', element: seo('projectManagement', <ProjectManagement />) },
          { path: 'law-2027', element: seo('law2027', <Law2027 />) },
          { path: 'pricing', element: seo('pricing', <Pricing />) },
          { path: 'industries', element: seo('industries', <Industries />) },
          { path: 'industry/:slug?', element: seo('industry', <IndustryDetail />) },
          { path: 'resources', element: seo('resources', <Resources />) },
          { path: 'trainings', element: seo('trainings', <Trainings />) },
          { path: 'getting-started', element: seo('gettingStarted', <GettingStarted />) },
          { path: 'blog/:slug?', element: seo('blog', <BlogPost />) },
          { path: 'legal/terms', element: seo('legalTerms', <Legal kind="terms" />) },
          { path: 'legal/privacy', element: seo('legalPrivacy', <Legal kind="privacy" />) },
          { path: 'faq', element: seo('faq', <Faq />) },
          { path: 'about', element: seo('about', <About />) },
          { path: 'contact', element: seo('contact', <Contact />) },
          { path: 'free-trial', element: seo('freeTrial', <FreeTrial />) },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },

  // ── Signup funnel (Tailwind DS, CSR-only — excluded from the prerender matrix) ─
  {
    path: ':lang',
    element: <FunnelRoot />,
    children: [
      {
        element: (
          <Suspense fallback={<Spinner />}>
            <Outlet />
          </Suspense>
        ),
        children: [
          { path: 'plan', element: <PlanPage /> },
          { path: 'confirm', element: <ConfirmPage /> },
          { path: 'account', element: <AccountPage /> },
          { path: 'verify-email', element: <VerifyEmailPage /> },
          { path: 'payment', element: <PaymentPage /> },
          { path: 'done', element: <DonePage /> },
        ],
      },
    ],
  },
];

export type { Lang };
